import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState('');

  const eventTypeColors = {
    exam: 'bg-red-100 text-red-800',
    holiday: 'bg-green-100 text-green-800',
    meeting: 'bg-yellow-100 text-yellow-800',
    sports: 'bg-blue-100 text-blue-800',
    cultural: 'bg-purple-100 text-purple-800',
    field_trip: 'bg-orange-100 text-orange-800',
    other: 'bg-gray-100 text-gray-800'
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [filterClass]);

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_BASE}/classes`);
      setClasses(res.data);
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      let url = `${API_BASE}/events?`;
      if (filterClass) url += `classId=${filterClass}&`;
      const res = await axios.get(url);
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const isUpcoming = (d) => new Date(d) >= new Date(new Date().setHours(0, 0, 0, 0));

  const upcomingEvents = events.filter(e => isUpcoming(e.eventDate));
  const pastEvents = events.filter(e => !isUpcoming(e.eventDate));

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex justify-between items-center px-6 py-3">
            <h1 className="text-xl font-semibold text-gray-800">Events</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">All Events</h2>

          <div className="bg-white rounded-lg shadow p-4 mb-6 flex gap-4 items-center">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Filter by Class</label>
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">All Classes</option>
                {classes.map(c => (
                  <option key={c._id} value={c._id}>{c.className} - {c.section}</option>
                ))}
              </select>
            </div>
            {filterClass && (
              <button
                onClick={() => setFilterClass('')}
                className="text-sm text-indigo-600 hover:underline mt-4"
              >
                Clear filter
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-indigo-600">{events.length}</p>
              <p className="text-gray-500 text-sm">Total Events</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{upcomingEvents.length}</p>
              <p className="text-gray-500 text-sm">Upcoming</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-gray-400">{pastEvents.length}</p>
              <p className="text-gray-500 text-sm">Past</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No events found.
            </div>
          ) : (
            <>
              {upcomingEvents.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Upcoming Events</h3>
                  <div className="space-y-3">
                    {upcomingEvents.map((event) => (
                      <div key={event._id} className="bg-white rounded-lg shadow p-5 border-l-4 border-indigo-500">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900 text-lg">{event.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${eventTypeColors[event.eventType] || eventTypeColors.other}`}>
                            {event.eventType.replace('_', ' ')}
                          </span>
                          {event.isSchoolWide && (
                            <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-xs">School-wide</span>
                          )}
                        </div>
                        <p className="text-gray-600 mb-2">{event.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>📅 {formatDate(event.eventDate)}</span>
                          {event.classId && <span>🏫 {event.classId.className} - {event.classId.section}</span>}
                          {event.createdBy && <span>👨‍🏫 {event.createdBy.name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pastEvents.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Past Events</h3>
                  <div className="space-y-3">
                    {pastEvents.map((event) => (
                      <div key={event._id} className="bg-white rounded-lg shadow p-5 border-l-4 border-gray-300 opacity-75">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">{event.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${eventTypeColors[event.eventType] || eventTypeColors.other}`}>
                            {event.eventType.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm">{event.description}</p>
                        <p className="text-sm text-gray-400 mt-1">📅 {formatDate(event.eventDate)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
